import { useState, useEffect } from 'react';
import { Bug, Lightbulb, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { submitFeedback } from '@/lib/feedback';
import { getConsoleLogs } from '@/lib/console-capture';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

type FeedbackType = 'bug' | 'feature';

interface FeedbackModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FeedbackModal({ open, onOpenChange }: FeedbackModalProps) {
  const { user, profile } = useAuth();
  const [type, setType] = useState<FeedbackType>('bug');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [consoleLogs, setConsoleLogs] = useState('');
  const [logsExpanded, setLogsExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setType('bug');
      setSubject('');
      setDescription('');
      setConsoleLogs(getConsoleLogs());
      setLogsExpanded(false);
      setIsSubmitting(false);
    }
  }, [open]);

  useEffect(() => {
    if (type === 'bug') {
      setConsoleLogs(getConsoleLogs());
    }
  }, [type]);

  const canSubmit = subject.trim().length > 0 && description.trim().length > 0 && !isSubmitting;

  async function handleSubmit() {
    if (!canSubmit) return;

    setIsSubmitting(true);
    const result = await submitFeedback({
      type,
      subject: subject.trim(),
      description: description.trim(),
      consoleLogs: type === 'bug' ? consoleLogs : undefined,
      userEmail: user?.email || '',
      userName: `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() || 'Unknown',
      userRole: profile?.role,
      organizationId: profile?.organization_id || undefined,
      userAgent: navigator.userAgent,
      currentUrl: window.location.href,
      timestamp: new Date().toISOString(),
    });

    setIsSubmitting(false);

    if (result.success) {
      toast.success('Feedback submitted', {
        description: 'Thank you for your feedback. We will review it shortly.',
      });
      onOpenChange(false);
    } else {
      toast.error('Failed to submit feedback', {
        description: result.error || 'Please try again later.',
      });
    }
  }

  return (
    <ResponsiveModal open={open} onOpenChange={onOpenChange}>
      <ResponsiveModalContent maxWidth="md">
        <ResponsiveModalHeader>
          <ResponsiveModalTitle>Provide Feedback</ResponsiveModalTitle>
          <ResponsiveModalDescription>
            Report a bug or suggest a feature to help us improve.
          </ResponsiveModalDescription>
        </ResponsiveModalHeader>

        <ResponsiveModalBody className="space-y-4">
          <div className="flex gap-2">
            <Button
              type="button"
              variant={type === 'bug' ? 'default' : 'outline'}
              className={cn('flex-1', type === 'bug' && 'shadow-sm')}
              onClick={() => setType('bug')}
            >
              <Bug className="mr-2 h-4 w-4" />
              Bug Report
            </Button>
            <Button
              type="button"
              variant={type === 'feature' ? 'default' : 'outline'}
              className={cn('flex-1', type === 'feature' && 'shadow-sm')}
              onClick={() => setType('feature')}
            >
              <Lightbulb className="mr-2 h-4 w-4" />
              Feature Request
            </Button>
          </div>

          <div className="space-y-2">
            <Label htmlFor="feedback-subject">Subject</Label>
            <Input
              id="feedback-subject"
              placeholder={type === 'bug' ? 'Brief description of the issue' : 'Brief description of the feature'}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={200}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="feedback-description">Description</Label>
            <Textarea
              id="feedback-description"
              placeholder={
                type === 'bug'
                  ? 'What happened? What did you expect to happen? Steps to reproduce...'
                  : 'Describe the feature you would like and how it would help you...'
              }
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              className="resize-none"
            />
          </div>

          {type === 'bug' && consoleLogs && (
            <div className="space-y-2">
              <button
                type="button"
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setLogsExpanded(!logsExpanded)}
              >
                {logsExpanded ? (
                  <ChevronUp className="h-3.5 w-3.5" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5" />
                )}
                Console logs will be attached
              </button>
              {logsExpanded && (
                <pre className="max-h-48 overflow-auto rounded-md bg-muted p-3 text-xs text-muted-foreground">
                  {consoleLogs}
                </pre>
              )}
            </div>
          )}
        </ResponsiveModalBody>

        <ResponsiveModalFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Submit
          </Button>
        </ResponsiveModalFooter>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}
